import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Calendar, Plus } from 'lucide-react';
import { api } from '../api';
import type { Party, WeeklyBatch } from '../api';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import { Btn, Card, EmptyState, RowSkeleton, StatePill } from '../ui/components';

export function BatchesPage() {
  const nav = useNavigate();
  const readOnly = useReadOnly();

  const batches = useQuery<WeeklyBatch[]>({
    queryKey: ['batches'],
    queryFn: () => api.get<WeeklyBatch[]>('/api/batches'),
  });
  const uplines = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });

  const uplineById = useMemo(
    () => new Map((uplines.data ?? []).map((p) => [p.id, p.displayName])),
    [uplines.data],
  );

  // Stat counts
  const counts = useMemo(() => {
    const list = batches.data ?? [];
    return {
      draft: list.filter((b) => b.state === 'draft').length,
      verified: list.filter((b) => b.state === 'verified').length,
      archived: list.filter((b) => b.state === 'archived').length,
    };
  }, [batches.data]);

  return (
    <AppShell
      title="Weekly batches"
      toolbar={
        <PageToolbar
          title="Weekly batches"
          primary={
            !readOnly ? (
              <Btn size="sm" onClick={() => nav('/batches/new')}>
                <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                <span className="hidden sm:inline">New batch</span>
                <span className="sm:hidden">New</span>
              </Btn>
            ) : undefined
          }
        />
      }
    >
      {/* ── Stat bar ─────────────────────────────────────────────────────── */}
      {!batches.isLoading && (batches.data?.length ?? 0) > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <StatBadge label="Draft" count={counts.draft} tone="amber" />
          <StatBadge label="Verified" count={counts.verified} tone="green" />
          <StatBadge label="Archived" count={counts.archived} tone="neutral" />
        </div>
      )}

      {/* ── Content ──────────────────────────────────────────────────────── */}
      {batches.isLoading ? (
        <Card padding={false}>
          <div className="px-4 py-3">
            <RowSkeleton rows={5} />
          </div>
        </Card>
      ) : (batches.data?.length ?? 0) === 0 ? (
        <EmptyState
          icon={Calendar}
          title="No batches yet"
          description="Create your first batch when your upline sends the weekly P&L sheet."
          action={
            !readOnly ? (
              <Btn size="sm" onClick={() => nav('/batches/new')}>
                New batch
              </Btn>
            ) : undefined
          }
        />
      ) : (
        <>
          {/* Desktop: table */}
          <div className="hidden md:block">
            <Card padding={false} className="overflow-x-auto">
              <table className="min-w-full text-small">
                <thead className="bg-surface-raised">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Upline
                    </th>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Week (Monday)
                    </th>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      State
                    </th>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Created
                    </th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {batches.data!.map((b) => (
                    <tr key={b.id} className="transition-colors hover:bg-surface-raised/60">
                      <td className="px-4 py-3 font-medium text-ink">
                        {uplineById.get(b.uplinePartyId) ?? `#${b.uplinePartyId}`}
                      </td>
                      <td className="px-4 py-3 text-ink">{b.weekMonday}</td>
                      <td className="px-4 py-3">
                        <StatePill state={b.state} />
                      </td>
                      <td className="px-4 py-3 text-caption text-ink-muted">
                        {b.createdAt.slice(0, 10)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/batches/${b.id}`}
                          className="inline-flex items-center gap-1 text-caption text-brand hover:underline"
                        >
                          Open
                          <ArrowRight className="h-3 w-3" strokeWidth={2} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {/* Mobile: card list */}
          <div className="space-y-2 md:hidden">
            {batches.data!.map((b) => (
              <BatchMobileCard
                key={b.id}
                batch={b}
                uplineName={uplineById.get(b.uplinePartyId) ?? `#${b.uplinePartyId}`}
              />
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function StatBadge({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'amber' | 'green' | 'neutral';
}) {
  const toneClasses = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    neutral: 'bg-surface-raised text-ink-muted border-border',
  };
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-caption font-medium',
        toneClasses[tone],
      ].join(' ')}
    >
      {label}
      <span className="ml-0.5 tabular-nums">{count}</span>
    </span>
  );
}

function BatchMobileCard({
  batch,
  uplineName,
}: {
  batch: WeeklyBatch;
  uplineName: string;
}) {
  return (
    <Link
      to={`/batches/${batch.id}`}
      className="flex items-center gap-3 rounded-card border border-border bg-surface p-4 shadow-card transition-colors hover:bg-surface-raised active:bg-surface-raised"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-small font-semibold text-ink">{uplineName}</span>
          <StatePill state={batch.state} />
        </div>
        <div className="mt-0.5 text-caption text-ink-muted">{batch.weekMonday}</div>
        <div className="mt-0.5 text-caption text-ink-muted">
          Created {batch.createdAt.slice(0, 10)}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.75} />
    </Link>
  );
}
